/**
 * GoalProgressStrip — persistent strip above the composer showing goal progress.
 *
 * Displays:
 * - Current phase badge (Planning → Executing → Verifying)
 * - Plan progress: "3/7 steps complete (2 verified)"
 * - Budget gauge (cost/time/turns consumed vs budget)
 * - Pause state + reason
 */
import * as React from "react";
import {
  Box, Chip, LinearProgress, Stack, Tooltip, Typography,
} from "@mui/material";
import {
  CheckCircle, Circle, Loader, Pause, Target, XCircle,
} from "lucide-react";

import type { GoalState } from "../../chat/goalTypes";
import { CHAT_COLUMN_MAX_WIDTH, CHAT_COLUMN_GUTTER } from "./layout";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function phaseBadge(phase: GoalState["phase"]) {
  switch (phase) {
    case "planning":
      return { label: "Planning", icon: <Circle size={12} />, color: "info" as const };
    case "executing":
      return { label: "Executing", icon: <Loader size={12} className="wp-spin" />, color: "primary" as const };
    case "verifying":
      return { label: "Verifying", icon: <Target size={12} />, color: "warning" as const };
    case "complete":
      return { label: "Complete", icon: <CheckCircle size={12} />, color: "success" as const };
    case "paused":
      return { label: "Paused", icon: <Pause size={12} />, color: "default" as const };
    default:
      return { label: "Idle", icon: <Circle size={12} />, color: "default" as const };
  }
}

function pauseReasonLabel(reason: GoalState["pauseReason"]): string {
  switch (reason) {
    case "user_paused": return "Paused by user";
    case "budget_exhausted": return "Budget exhausted";
    case "stall_detected": return "Stalled — same issue detected repeatedly";
    case "infra_error": return "Infrastructure error";
    case "blocked": return "Blocked — waiting on external dependency";
    default: return "Paused";
  }
}

function formatBudgetPct(spent: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.round((spent / max) * 100));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface GoalProgressStripProps {
  goal: GoalState;
}

export function GoalProgressStrip({ goal }: GoalProgressStripProps) {
  const badge = phaseBadge(goal.phase);

  // Plan progress
  const totalItems = goal.plan.length;
  const completedItems = goal.plan.filter(
    (i) => i.status === "completed" || i.status === "skipped",
  ).length;
  const verifiedItems = goal.plan.filter((i) => i.verified_at != null).length;

  // Budget gauge — pick the most-consumed dimension
  const costPct = formatBudgetPct(goal.spent.cost_usd, goal.budget.max_cost_usd);
  const turnsPct = formatBudgetPct(goal.spent.turns, goal.budget.max_turns);
  const durPct = formatBudgetPct(goal.spent.duration_s, goal.budget.max_duration_s);
  const maxPct = Math.max(costPct, turnsPct, durPct);
  const budgetWarn = maxPct >= 80;

  return (
    <Box
      data-testid="goal-progress-strip"
      sx={{
        width: "100%",
        maxWidth: CHAT_COLUMN_MAX_WIDTH,
        mx: "auto",
        px: CHAT_COLUMN_GUTTER,
        pb: 0.5,
      }}
      aria-live="polite"
      role="status"
      aria-label={`Goal: ${goal.objective}`}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        {/* Phase badge */}
        <Chip
          icon={badge.icon}
          label={badge.label}
          color={badge.color}
          size="small"
          variant="outlined"
        />

        {/* Plan progress (only when plan has items) */}
        {totalItems > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
            {completedItems}/{totalItems} steps
            {verifiedItems > 0 && ` (${verifiedItems} verified)`}
          </Typography>
        )}

        {/* Budget gauge */}
        <Tooltip
          title={`Cost: $${goal.spent.cost_usd.toFixed(2)}/$${goal.budget.max_cost_usd.toFixed(2)} · Turns: ${goal.spent.turns}/${goal.budget.max_turns} · Time: ${goal.spent.duration_s}s/${goal.budget.max_duration_s}s`}
        >
          <Box sx={{ flex: 1, minWidth: 60, maxWidth: 120 }}>
            <LinearProgress
              variant="determinate"
              value={maxPct}
              color={budgetWarn ? "warning" : "primary"}
              sx={{ height: 4, borderRadius: 2 }}
              aria-label="goal budget usage"
            />
          </Box>
        </Tooltip>

        {/* Budget warning indicator */}
        {goal.budgetWarningDimension && (
          <Chip
            icon={<XCircle size={12} />}
            label={`${goal.budgetWarningDimension} ${maxPct}%`}
            size="small"
            color="warning"
            variant="filled"
          />
        )}
      </Stack>

      {/* Pause reason */}
      {goal.phase === "paused" && goal.pauseReason && (
        <Typography
          variant="body2"
          color="warning.main"
          sx={{ mt: 0.5, fontSize: "0.75rem" }}
          data-testid="goal-pause-reason"
        >
          {pauseReasonLabel(goal.pauseReason)}
        </Typography>
      )}

      {/* Strategy note (when stalled) */}
      {goal.strategyNote && goal.phase === "paused" && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.25, fontSize: "0.75rem", fontStyle: "italic" }}
          data-testid="goal-strategy-note"
        >
          Strategy: {goal.strategyNote}
        </Typography>
      )}
    </Box>
  );
}

export default GoalProgressStrip;
